// @ts-strict-ignore
import isEqual from 'lodash/isEqual';

import {canUseViewN} from 'common/components/data-table/utils';
import {Timezone} from 'common/constants';
import {
  ChartType,
  ConversionWindowUnit,
  FunnelReentryModeType,
  FUNNELS_MATH_TYPES,
  MetricType,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {getFirstSegment, isCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {hasSupportedAttributionType} from 'common/query-builder/query-builder/util';
import {
  assignDefaultTimeReference,
  isAggregateUserFreqByGroupSegmentation,
  isBehaviorSegmentation,
  isEventBasedBehavioralSegmentation,
  isMultiAttributionSegmentation,
  isPageDurationSegmentation,
  isTTCSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {
  FiltersOperator,
  isMetricEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryEventEntry,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {AdditionalContext, ConversionCriteria, QueryContext} from 'common/query-context';
import {ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {
  FUNNEL_MINUTE_CHART_MAX_HOUR_DURATION,
  MINUTE_CHART_MAX_HOUR_DURATION,
} from 'common/report/insights/models/constants';
import {getNumberOfHoursInTimeClause} from 'common/report/insights/models/time-clause';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {CurrentProject} from 'common/report/model-resources/projects';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {QueryContextFnParams} from 'common/report/report-manager/types';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {renameEvent} from 'common/report/util/events';
import {WindowType} from 'common/types/reports/query';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {isEventFilter, isUserFilter} from 'common/widgets/property-filter-menu/models/utils';
import {mapEventEntryToBookmarkExclusionStep} from 'common/report/funnels/models/format';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {getHeaderCount} from 'common/report/insights/query-builder/qb-update';
import {isMetricLevelDataGroupsEnabled} from 'common/report/util/feature-sampling';

import {Report} from '../models/report';
import {isFunnelShowClause, isRetentionShowClause} from '../models/utils';
import {getSortConfig} from '../util';
// TODO move common/report/util/insights to common/report/insights/util
import {QueryMode} from '../util/constants';
import {getShowSection} from '../util/events';
import {getFilterSection, getPropertyFilters} from '../util/filters';
import {getSegmentSections, getSelectedSegments} from '../util/segments';
import {getDateRange, getTimeSection} from '../util/time';
import {getMetricEntriesFromShowClauses} from '../util/metrics';

function getEventsFromQueryContext(queryContext: QueryContext): QueryEventEntry[] {
  let events = queryContext.events || [];
  if (queryContext.viewAsFunnelContext) {
    events = [];
    const selectedSteps = queryContext.viewAsFunnelContext.selectedSteps;
    let selectedStepIndex = 0;
    queryContext.viewAsFunnelContext.topPathsFlowSteps.forEach((event, idx) => {
      if (selectedSteps.has(idx)) {
        events.push(queryContext.events[selectedStepIndex]);
        selectedStepIndex++;
      } else {
        events.push({
          event: {
            id: null,
            label: event,
            value: event,
          },
          filters: [],
          filtersOperator: FiltersOperator.And,
          type: ResourceType.Event,
        });
      }
    });
    // Add selected steps that are not included in top paths
    while (selectedStepIndex < queryContext.events.length) {
      events.push(queryContext.events[selectedStepIndex]);
      selectedStepIndex += 1;
    }
  }
  return events;
}

function createMetricEntry(
  params: QueryContextFnParams,
  events: QueryEventEntry[],
  metricType: MetricType,
  options: {
    conversionCriteria?: ConversionCriteria;
    multiAttribution?: MultiAttribution;
  },
): MetricEntry {
  const defaultToMathSession = params.project.hasFeatureFlag(ProjectFeatureFlag.FunnelsMathSessions);
  const defaultToConversionSession = [
    ProjectFeatureFlag.FunnelsMathSessions,
    ProjectFeatureFlag.FunnelsConversionSessions,
  ].some((ff) => params.project.hasFeatureFlag(ff));
  if (metricType === MetricType.Funnel) {
    const {conversionWindow, exclusionSteps = [], holdingPropertyConstant = []} = options?.conversionCriteria || {};
    const {
      unit: conversionWindowUnit = defaultToConversionSession ? ConversionWindowUnit.Session : ConversionWindowUnit.Day,
      value: conversionWindowDuration = defaultToConversionSession ? 1 : 7,
    } = conversionWindow || {};
    return {
      type: TopLevelMetricType.MetricEntry,
      isExpanded: true,
      behavior: {
        type: MetricType.Funnel,
        name: null,
        conversionWindowDuration,
        conversionWindowUnit,
        funnelReentryMode: FunnelReentryModeType.Basic,
        aggregateBy: holdingPropertyConstant,
        exclusions: exclusionSteps.map((step) => {
          return mapEventEntryToBookmarkExclusionStep(events.length, step);
        }),
        behaviors: events.map((event) => ({
          type: MetricType.Event,
          name: event.event.value,
          filters: event.filters,
          filtersOperator: event.filtersOperator,
          ...(event.event.renamed ? {renamed: event.event.renamed} : {}),
          filtersDeterminer: event.filtersOperator === FiltersOperator.Or ? `any` : `all`,
          ...(event.event.custom
            ? {
                id: event.event.id,
                type: MetricType.CustomEvent,
              }
            : {}),
        })) as MetricEntryBehavior[],
      },
      measurement: {
        aggregationOperator: defaultToMathSession
          ? FUNNELS_MATH_TYPES.SESSION
          : FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
        stepIndex: null,
        multiAttribution: options?.multiAttribution,
      },
    };
  }

  if (metricType === MetricType.Retention) {
    return {
      type: TopLevelMetricType.MetricEntry,
      isExpanded: true,
      behavior: {
        type: MetricType.Retention,
        name: null,
        retentionType: `birth`,
        retentionAlignmentType: RetentionAlignmentType.Birth,
        retentionUnit: TimeUnit.Day,
        retentionUnboundedMode: `none`,
        retentionCustomBucketSizes: [],
        behaviors: events
          .map((event) => ({
            type: MetricType.Event,
            name: event.event.value,
            filters: event.filters,
            filtersOperator: event.filtersOperator,
            ...(event.event.custom
              ? {
                  id: event.event.id,
                  type: MetricType.CustomEvent,
                }
              : {}),
          }))
          .slice(0, 2) as MetricEntryBehavior[],
      },
      measurement: {
        aggregationOperator: RETENTION_MATH_TYPES.RETENTION_RATE,
        retentionBucketIndex: 0,
        multiAttribution: options?.multiAttribution,
      },
    };
  }

  return null;
}

function getEntriesFromQueryContext(
  queryContext: QueryContext,
  params: QueryContextFnParams,
  currentReportMode: BookmarkType,
): Array<QueryMetricEntry | MetricEntry> | null {
  // flattened events
  const events = getEventsFromQueryContext(queryContext);
  // metric entries (every metric is a MetricEntry)
  const metrics = queryContext.metrics || [];

  if (events.length === 0 && metrics.length === 0) {
    return null;
  }

  const eventMultiAttribution = events.find((event) => event.multiAttribution)?.multiAttribution;
  const metricMultiAttribution = metrics.find((metric) => metric.measurement?.multiAttribution)?.measurement
    .multiAttribution;
  const existingMultiAttribution = eventMultiAttribution || metricMultiAttribution;
  const hasSupportedType = hasSupportedAttributionType(existingMultiAttribution?.type);
  const multiAttribution = hasSupportedType
    ? existingMultiAttribution
    : existingMultiAttribution
      ? DefaultMultiAttribution
      : null;

  if (currentReportMode === BookmarkType.Funnels) {
    // if one or more funnel behaviors exists, take the first funnel behavior
    const funnelMetric = metrics.find((metric) => metric.behavior.type === MetricType.Funnel);
    if (funnelMetric) {
      return [funnelMetric];
    }

    if (events.length === 0) {
      return null;
    }

    // if no funnel behaviors exists, merge all of the events into one funnel
    const conversionCriteria = queryContext.conversionCriteria;
    return [createMetricEntry(params, events, MetricType.Funnel, {conversionCriteria, multiAttribution})];
  }
  if (currentReportMode === BookmarkType.Retention) {
    // if one or more retention behaviors exists, take the first retention behavior
    const retentionMetric = metrics.find((metric) => metric.behavior.type === MetricType.Retention);
    if (retentionMetric) {
      return [retentionMetric];
    }
    // if no retention behaviors exists, take the first 2 events and createa a retention behavior
    return [createMetricEntry(params, events, MetricType.Retention, {multiAttribution})];
  }

  if (multiAttribution) {
    events.forEach((event) => (event.multiAttribution = multiAttribution));
  }

  return events;
}

function getSupportedChartTypes(insightsReportMode: BookmarkType) {
  if (insightsReportMode === BookmarkType.Funnels) {
    return [ChartType.FunnelSteps, ChartType.Line, ChartType.Bar, ChartType.Metric, ChartType.FunnelTopPaths];
  }

  if (insightsReportMode === BookmarkType.Retention) {
    return [ChartType.RetentionCurve, ChartType.Line, ChartType.Metric];
  }

  return [
    ChartType.Line,
    ChartType.StackedLine,
    ChartType.Bar,
    ChartType.StackedBar,
    ChartType.Pie,
    ChartType.Table,
    ChartType.Metric,
  ];
}

function flattenBehaviors(behaviors: MetricEntryBehavior[]): QueryEventEntry[] {
  return behaviors
    .filter((behavior) => !!behavior.name || !!behavior.id || !!behavior.behaviors)
    .flatMap((behavior) => {
      if (behavior.behaviors) {
        return flattenBehaviors(behavior.behaviors);
      }
      return {
        type: ResourceType.Event,
        event: {
          label: renameEvent(behavior.name),
          value: behavior.name,
          custom: behavior.type === MetricType.CustomEvent,
          id: behavior.id,
        },
        filters: behavior.filters,
        filtersOperator: behavior.filtersOperator,
      } as QueryEventEntry;
    });
}

export function getReportQueryContext(
  report: Report,
  {
    additionalContext,
    behaviorsResource,
    bookmarkType = BookmarkType.Insights,
    dataGroups,
    defaultReportState,
    metricsResource,
    metricType = MetricType.Event,
    originDashboard,
  }: {
    additionalContext?: AdditionalContext;
    behaviorsResource: BehaviorsResource;
    bookmarkType: BookmarkType;
    currentProject: CurrentProject;
    dataGroups: DataGroup[];
    defaultReportState: Report;
    metricType?: MetricType;
    metricsResource: MetricsResource;
    originDashboard: DashboardOrigin;
  },
): QueryContext {
  if (isEqual(defaultReportState.toBookmarkData(), report.toBookmarkData())) {
    return null;
  }
  const bookmarkParams = report.toBookmarkData();

  const dateRange = getDateRange(report.sections.time.clauses).getAttributes();
  const dataGroupId = report.sections.getDataGroupId();
  const showSection = bookmarkParams.sections.show;

  const events: QueryEventEntry[] = [];

  const entries = getMetricEntriesFromShowClauses({
    showClauses: showSection,
    behaviorsResource,
    metricsResource,
    dataGroups,
  });

  const metrics: MetricEntry[] = entries
    .filter((entry): entry is MetricEntry => isMetricEntry(entry))
    .map((entry) => {
      return {
        ...entry,
        isHidden: false,
      };
    });
  entries.forEach(function (entry) {
    if (
      isMetricEntry(entry) &&
      [MetricType.Simple, MetricType.Funnel, MetricType.Retention, MetricType.RetentionFrequency].includes(
        entry.behavior.type,
      )
    ) {
      // flatten metric behaviors
      events.push(...flattenBehaviors(entry.behavior.behaviors));
    } else if (entry.behavior?.type === MetricType.Event || entry.behavior?.type === MetricType.CustomEvent) {
      const insightsOnlyEventValues = [`$top_events`, `$all_events`];

      if (!insightsOnlyEventValues.includes(entry.behavior.name)) {
        events.push({
          type: ResourceType.Event,
          event: {
            label: ``,
            value: entry.behavior.name,
            id: entry.behavior.id,
            custom: Boolean(entry.behavior.id),
          },
          filters: entry.behavior.filters.filter(
            (f) => (isEventFilter(f) || isUserFilter(f)) && !isBehaviorSegmentation(f),
          ),
          filtersOperator: entry.behavior.filtersOperator,
          ...(entry.measurement.multiAttribution ? {multiAttribution: entry.measurement.multiAttribution} : {}),
        });
      }
    }
  });

  const comparedDateRange = report.timeComparison;
  const segments = getSelectedSegments(report.sections.group.clauses);
  const filters = getPropertyFilters(report.sections.filter.clauses);

  const chartType = report.displayOptions?.chartType;
  const theme = report.displayOptions?.theme;

  const filteredSegments = segments.filter((segment) => {
    return !isPageDurationSegmentation(getFirstSegment(segment));
  });
  const filteredFilters = filters.filter((filter) => {
    return !isPageDurationSegmentation(filter);
  });

  return {
    bookmarkType,
    querySamplingEnabled: report.querySamplingEnabled,
    dataGroupId,
    dateRange,
    events,
    metrics,
    comparedDateRange,
    metricType,
    chartType,
    ...(originDashboard && {originDashboard}),
    segments: filteredSegments,
    filters: filteredFilters,
    ...(theme && {theme}),
    ...(additionalContext ?? {}),
  };
}

const BookmarkTypeToQueryMode: Partial<Record<BookmarkType, QueryMode>> = {
  [BookmarkType.Insights]: QueryMode.Events,
  [BookmarkType.Funnels]: QueryMode.Funnels,
  [BookmarkType.Retention]: QueryMode.Retention,
  [BookmarkType.RetentionFrequency]: QueryMode.Retention,
};

export function getReportFromQueryContext(
  queryContext: QueryContext,
  params: QueryContextFnParams,
  insightsReportMode: BookmarkType,
  metricsResource: MetricsResource,
  behaviorsResource: BehaviorsResource,
) {
  const defaultToMathSession = params.project.hasFeatureFlag(ProjectFeatureFlag.FunnelsMathSessions);
  const defaultToConversionSession = [
    ProjectFeatureFlag.FunnelsMathSessions,
    ProjectFeatureFlag.FunnelsConversionSessions,
  ].some((ff) => params.project.hasFeatureFlag(ff));

  const report = Report.createDefaultReport({
    queryMode: BookmarkTypeToQueryMode[insightsReportMode] ?? QueryMode.Events,
    metricLevelDataGroups: isMetricLevelDataGroupsEnabled(params.project),
    defaultToMathSession,
    defaultToConversionSession,
  });

  if (queryContext.chartType) {
    let chartType = queryContext.chartType;
    // Reset the default chart type if the current chart is not supported by the current report mode
    const supportedChartTypes = getSupportedChartTypes(insightsReportMode);
    if (insightsReportMode === BookmarkType.Insights && !supportedChartTypes.includes(chartType)) {
      chartType = ChartType.Line;
    } else if (insightsReportMode === BookmarkType.Funnels && !supportedChartTypes.includes(chartType)) {
      chartType = ChartType.FunnelSteps;
    } else if (insightsReportMode === BookmarkType.Retention && !supportedChartTypes.includes(chartType)) {
      chartType = ChartType.RetentionCurve;
    }
    report.overrideChartType(chartType);
  }

  const entries = getEntriesFromQueryContext(queryContext, params, insightsReportMode);
  if (entries) {
    report.sections = report.sections.replaceSection(
      getShowSection({
        entries,
        dataGroupId: queryContext.dataGroupId,
      }),
    );
  }

  report.sections.setDataGroupId({
    dataGroupId: queryContext.dataGroupId,
    customEvents: params.customEvents,
    metricsResource,
    behaviorsResource,
  });

  if (queryContext.comparedDateRange) {
    report.timeComparison = queryContext.comparedDateRange;
  }

  const showSection = report.sections.show.toBookmarkData();
  const selectedTimeUnit = showSection?.[0]?.unit;
  if (queryContext.dateRange) {
    const timeSection = getTimeSection(queryContext.dateRange, {selectedTimeUnit, useDefaultTimeUnit: true});
    const timeClause = timeSection.clauses[0];

    // retention metric does not support "minute", "hour", or "quarter" as time unit
    if (
      showSection.some(isRetentionShowClause) &&
      [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Quarter].includes(timeClause.unit)
    ) {
      timeClause.unit = TimeUnit.Day;
    }

    const timezone: Timezone = params.project?.timezone ?? null;

    // funnels metrics don't support "minute" w/ date ranges > 2 hours
    if (
      (timeClause.unit as TimeUnit) === TimeUnit.Minute &&
      showSection.some(isFunnelShowClause) &&
      getNumberOfHoursInTimeClause(timeClause, timezone, params?.project?.projectAsOfDate) >
        FUNNEL_MINUTE_CHART_MAX_HOUR_DURATION
    ) {
      timeClause.unit = TimeUnit.Hour;
    }

    // insights metrics don't support "minute" w/ date ranges > 48 hours
    if (
      (timeClause.unit as TimeUnit) === TimeUnit.Minute &&
      getNumberOfHoursInTimeClause(timeClause, timezone, params?.project?.projectAsOfDate) >
        MINUTE_CHART_MAX_HOUR_DURATION
    ) {
      timeClause.unit = TimeUnit.Hour;
    }

    report.sections.time = timeSection;
  }

  if (queryContext.segments) {
    const segments = [];
    // TODO query cohorts like a property
    let hasCohortSegment = false;
    queryContext.segments.forEach((segment) => {
      if (isCohortSegment(segment)) {
        if (hasCohortSegment === false) {
          segments.push(segment);
        }
        hasCohortSegment = true;
      } else if (isMultiAttributionSegmentation(getFirstSegment(segment))) {
        segments.push(segment);
      } else if (isEventBasedBehavioralSegmentation(getFirstSegment(segment))) {
        let windowType: WindowType = null;
        if (showSection.every(isFunnelShowClause)) {
          windowType = WindowType.BETWEEN;
        } else if (showSection.every(isRetentionShowClause)) {
          windowType = WindowType.AFTER;
        }
        const behavior = assignDefaultTimeReference(segment[ResourceType.User].behavior, windowType);
        segments.push({
          ...segment,
          [ResourceType.User]: {
            ...segment[ResourceType.User],
            behavior,
          },
        });
      } else if (isTTCSegmentation(getFirstSegment(segment))) {
        if (insightsReportMode === BookmarkType.Insights || insightsReportMode === BookmarkType.Funnels) {
          segments.push(segment);
        }
      } else {
        segments.push(segment);
      }
    });

    getSegmentSections(segments).forEach((section) => {
      report.sections = report.sections.replaceSection(section);
    });
  }

  if (queryContext.filters) {
    const filters = [];
    queryContext.filters.forEach((filter) => {
      // This is for frequency per User or aggregate frequency per User to have "Per interval" or "Per step"
      if (isEventBasedBehavioralSegmentation(filter) || isAggregateUserFreqByGroupSegmentation(filter)) {
        let windowType: WindowType = null;
        if (showSection.every(isFunnelShowClause)) {
          windowType = WindowType.BETWEEN;
        } else if (showSection.every(isRetentionShowClause)) {
          windowType = WindowType.AFTER;
        }
        filters.push({
          ...filter,
          behavior: assignDefaultTimeReference(filter.behavior, windowType),
        });
      } else if (isTTCSegmentation(filter)) {
        if (insightsReportMode === BookmarkType.Insights || insightsReportMode === BookmarkType.Funnels) {
          filters.push(filter);
        }
      } else {
        filters.push(filter);
      }
    });

    report.sections = report.sections.replaceSection(getFilterSection(filters, null));
  }

  if (queryContext.querySamplingEnabled) {
    report.querySamplingEnabled = true;
  }

  report.sorting = getSortConfig({
    headerCount: getHeaderCount({report}),
    bookmarkSortConfig: report.sorting,
    numMetrics: report.sections.show.clauses.length,
    hasPivotTablesEnabled: true,
    canUseViewN: canUseViewN(),
    useDefaultViewNForNewSegments: true,
  });

  if (queryContext.theme) {
    report.displayOptions.theme = queryContext.theme;
  }

  return report;
}
